import React, {useCallback} from 'react';
import {Box, Button, Typography} from '@mui/material';
import {useDropzone} from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

interface ErrorMsgsObj {
  exceedFileNum?: string,
  exceedFileSize?: string,
  customErr1?: string,
  customErr2?: string,
  customErr3?: string,
}

interface UploadProps {
  uploadCallback: (file: File[]) => void;
  id?: string;
  placeholder?: string;
  buttonText?: string;
  buttonVariant?: 'text' | 'outlined' | 'contained';
  ErrorMsgs?: ErrorMsgsObj;
  sx?: any;
  icon?: any;
  accept?: any;
  maxFiles?: number;
  maxFileSize?: number; // todo: Implement a file size check
  displayFileRules?: boolean;
  disabled?: boolean;
}

const fallbackTransl = {
  placeholder: '<span style="font-weight:600">Drag & Drop <br> or</span>',
  buttonText: 'Choose Files'
};

const FaFileUpload = (props: UploadProps) => {

  const {
    uploadCallback,
    id,
    placeholder,
    buttonText,
    buttonVariant,
    sx,
    icon,
    accept,
    maxFiles,
    displayFileRules,
    disabled,
  } = props;

  const maxFileSize = props.maxFileSize || 5000 * 1024;

  // todo: Display Error Messages on File Validation
  const ErrorMsgs = props.ErrorMsgs || {
    exceedFileNum: `You have selected too many Files. Maximum upload number is ${maxFiles}.`,
    exceedFileSize: `You have selected too large File(s). Maximum upload size is ${maxFileSize / 1024} Kilobyte.`,
    customErr1: "This is custom Error 1",
    customErr2: "This is custom Error 2",
    customErr3: "This is custom Error 3",
  };

  const onDrop = useCallback((acceptedFiles: any[]): void => {
    if (acceptedFiles.length === 0)
      return;
    if (uploadCallback)
      uploadCallback(acceptedFiles)
  }, [uploadCallback]);

  const newSx = sx || {};
  const acceptedTypes = accept || {
    'image/*': ['.jpeg', '.jpg', '.png', '.dxf'],
    'application/sla': ['.stl', '.step', '.stp'],
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xls', '.xlsx'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
  }

  const {getRootProps, getInputProps, isDragActive, fileRejections} =
      useDropzone({
        onDrop,
        accept: acceptedTypes,
        maxFiles: maxFiles ? maxFiles : 1,
      });

  const style = {
    marginTop: '.5em',
    padding: '1em 0 1em 0',
    border: '2px dotted #79c4d4',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#005d83',
    height: '100%',
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&.disabled': {
      pointerEvents: 'none',
    },
  }

  return (
    <Box id={id ?? ''}
         {...getRootProps()}
         cy-data='drag-and-drop'
         className={disabled ? 'disabled' : ''}
         sx={{...style, ...newSx}}
    >
      <input
          {...getInputProps()}
          data-cy='upload-file-dnd'/>
      <Box>
        {icon
            ? icon
            : <CloudUploadIcon/>
        }
        {displayFileRules &&
            <Typography variant={'subtitle2'} sx={{margin: '.5rem 0 1rem 0'}}>|&nbsp;
              {Object.entries(acceptedTypes).map((value: any[]) =>
                  (Array.isArray(value[1]))
                      ? value[1].map((item) => (<span>{item} | </span>))
                      : null
              )}
            </Typography>
        }
        <Typography dangerouslySetInnerHTML={{__html: `${placeholder ?? fallbackTransl.placeholder}`}}
                    variant={'subtitle2'}></Typography>
        <Button disabled={disabled} size={'small'} variant={buttonVariant ?? 'outlined'} sx={{marginTop: '.5rem'}}>
          {buttonText ?? fallbackTransl.buttonText}
        </Button>
      </Box>
    </Box>
  )
}

export default FaFileUpload;
